/**
 * WorkoutsAccessToggle — owner-only inline toggle that grants or denies
 * workout-library visibility PER ATHLETE, independent of billing state.
 *
 * Sachin 2026-06: "make sure that it is specific to that person's profile
 * and not the whole family... when chosen to turn on or off, it stays
 * highlighted and is indicated whenever that profile is accessed."
 *
 * Semantics (mirrored server-side in /coach/athletes/{id}/workouts-access):
 *   • On    →  workouts_enabled = true   (always visible, this athlete only)
 *   • Off   →  workouts_enabled = false  (always hidden, this athlete only)
 *   • Auto  →  workouts_enabled = null   (billing-based rule)
 * Siblings are never affected — the flag lives on the athlete profile.
 *
 * Auth: owner-only (backend enforces 403; frontend hides the widget
 * when the current user isn't owner).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Dumbbell, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkoutsAccessToggle({ athleteId, athleteName, initialEnabled }) {
    const { user } = useAuth();
    // Local mirror of the athlete's `workouts_enabled`.
    // null/undefined = Auto (backend falls back to the billing check).
    const [enabled, setEnabled] = useState(initialEnabled);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setEnabled(initialEnabled);
    }, [initialEnabled]);

    if (user?.role !== "owner") return null;

    const state = enabled === true ? "on" : enabled === false ? "off" : "auto";
    const who = athleteName || "this athlete";

    const commit = async (nextValue) => {
        setSaving(true);
        const prev = enabled;
        setEnabled(nextValue);   // optimistic
        try {
            await axios.patch(
                `${API}/coach/athletes/${athleteId}/workouts-access`,
                { enabled: nextValue },
                { withCredentials: true },
            );
            const label = nextValue === true
                ? "Workouts turned ON"
                : nextValue === false
                    ? "Workouts turned OFF"
                    : "Reset to auto (billing-based)";
            toast.success(label, {
                description: `For ${who} only — siblings unaffected`,
            });
        } catch (e) {
            setEnabled(prev);   // revert
            const detail = e?.response?.data?.detail;
            toast.error("Couldn't update workouts access", {
                description: typeof detail === "string" ? detail : "Unknown error",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="workouts-access-toggle"
            className={`border rounded p-4 mb-6 bg-zinc-950/50 ${
                state === "on"
                    ? "border-emerald-700/70"
                    : state === "off"
                        ? "border-rose-700/70"
                        : "border-zinc-800"
            }`}
        >
            <div className="flex items-start gap-3">
                <Dumbbell className={`w-4 h-4 flex-shrink-0 mt-1 ${
                    state === "off" ? "text-rose-400" : "text-emerald-400"
                }`} />
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 font-semibold mb-1 flex items-center gap-2 flex-wrap">
                        Workouts access · owner override
                        <span
                            data-testid="workouts-access-state-badge"
                            className={`text-[9px] px-2 py-0.5 rounded-full border font-bold ${
                                state === "on"
                                    ? "border-emerald-500 bg-emerald-900/40 text-emerald-200"
                                    : state === "off"
                                        ? "border-rose-500 bg-rose-900/40 text-rose-200"
                                        : "border-zinc-600 bg-zinc-800/60 text-zinc-300"
                            }`}
                        >
                            {state === "on" ? "ON" : state === "off" ? "OFF" : "AUTO"}
                        </span>
                    </div>
                    <p className="text-sm text-zinc-200 leading-relaxed">
                        Manually control whether <span className="text-white font-medium">{who}</span> sees the workout library — independent of billing.
                    </p>
                    <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                        This setting applies to <span className="text-zinc-300">this athlete only</span> — siblings on the same family account are not affected.
                        Leave on <span className="text-zinc-300">Auto</span> to fall back to their subscription status (default behaviour).
                    </p>
                </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    data-testid="workouts-access-on"
                    onClick={() => commit(true)}
                    disabled={saving || state === "on"}
                    className={`text-[11px] tracking-[0.2em] uppercase px-3 py-2 rounded border transition-colors ${
                        state === "on"
                            ? "border-emerald-500 bg-emerald-900/40 text-emerald-100 font-semibold"
                            : "border-zinc-700 text-zinc-300 hover:border-emerald-500 hover:text-emerald-100"
                    } disabled:opacity-60`}
                >
                    {saving && state !== "on" && <Loader2 className="w-3 h-3 mr-1 inline animate-spin" />}
                    On · Show workouts
                </button>
                <button
                    type="button"
                    data-testid="workouts-access-off"
                    onClick={() => commit(false)}
                    disabled={saving || state === "off"}
                    className={`text-[11px] tracking-[0.2em] uppercase px-3 py-2 rounded border transition-colors ${
                        state === "off"
                            ? "border-rose-500 bg-rose-900/40 text-rose-100 font-semibold"
                            : "border-zinc-700 text-zinc-300 hover:border-rose-500 hover:text-rose-100"
                    } disabled:opacity-60`}
                >
                    {saving && state !== "off" && <Loader2 className="w-3 h-3 mr-1 inline animate-spin" />}
                    Off · Hide workouts
                </button>
                <button
                    type="button"
                    data-testid="workouts-access-auto"
                    onClick={() => commit(null)}
                    disabled={saving || state === "auto"}
                    className={`text-[11px] tracking-[0.2em] uppercase px-3 py-2 rounded border transition-colors inline-flex items-center gap-1.5 ${
                        state === "auto"
                            ? "border-zinc-500 bg-zinc-800 text-zinc-100 font-semibold"
                            : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-100"
                    } disabled:opacity-60`}
                >
                    {saving && state !== "auto" && <Loader2 className="w-3 h-3 animate-spin" />}
                    <RotateCcw className="w-3 h-3" />
                    Auto · By billing
                </button>
            </div>

            {state !== "auto" && (
                <div className="mt-3 text-[10px] text-zinc-500 leading-snug" data-testid="workouts-access-current-note">
                    Current: <span className={`font-semibold ${state === "on" ? "text-emerald-300" : "text-rose-300"}`}>
                        {state === "on"
                            ? `Workouts forced ON for ${who} regardless of billing`
                            : `Workouts forced OFF for ${who} regardless of billing`}
                    </span> — click <span className="text-zinc-300">Auto</span> to restore the default billing-based rule.
                </div>
            )}
        </div>
    );
}
